import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, vModelCheckbox as _vModelCheckbox, withDirectives as _withDirectives, normalizeClass as _normalizeClass, vModelText as _vModelText, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "project-notifications" }
const _hoisted_2 = { class: "loading-content" }
const _hoisted_3 = { class: "loading-text" }
const _hoisted_4 = { class: "error-content" }
const _hoisted_5 = { class: "error-title" }
const _hoisted_6 = { class: "error-message" }
const _hoisted_7 = { class: "form-group" }
const _hoisted_8 = { class: "toggle-group" }
const _hoisted_9 = { class: "toggle-label" }
const _hoisted_10 = { class: "toggle-wrapper" }
const _hoisted_11 = { class: "toggle-status" }
const _hoisted_12 = { class: "form-group" }
const _hoisted_13 = { class: "toggle-group" }
const _hoisted_14 = { class: "toggle-label" }
const _hoisted_15 = { class: "toggle-description" }
const _hoisted_16 = { class: "toggle-wrapper" }
const _hoisted_17 = ["disabled"]
const _hoisted_18 = { class: "toggle-status" }
const _hoisted_19 = {
  key: 0,
  class: "threshold-input"
}
const _hoisted_20 = {
  for: "balance-threshold",
  class: "threshold-label"
}
const _hoisted_21 = { class: "input-group" }
const _hoisted_22 = { class: "input-prefix" }
const _hoisted_23 = ["placeholder"]
const _hoisted_24 = {
  key: 0,
  class: "error-message"
}
const _hoisted_25 = {
  key: 0,
  class: "form-group email-types-section"
}
const _hoisted_26 = { class: "section-title" }
const _hoisted_27 = { class: "section-description" }
const _hoisted_28 = { class: "email-types-grid" }
const _hoisted_29 = { class: "email-type-header" }
const _hoisted_30 = { class: "email-type-info" }
const _hoisted_31 = { class: "email-type-name" }
const _hoisted_32 = { class: "email-type-description" }
const _hoisted_33 = { class: "email-type-toggle" }
const _hoisted_34 = { class: "toggle-wrapper" }
const _hoisted_35 = ["onUpdate:modelValue", "id", "onChange"]
const _hoisted_36 = ["for"]
const _hoisted_37 = { class: "toggle-status toggle-status-sm" }
const _hoisted_38 = {
  key: 0,
  class: "email-type-meta"
}
const _hoisted_39 = { class: "last-sent" }
const _hoisted_40 = { class: "form-actions" }

import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useNotifications } from '@/composables/useNotifications';

// Components
import AppCard from '../../base/AppCard.vue';
import AppButton from '../../base/AppButton.vue';
import AppPageHeader from '../../base/AppPageHeader.vue';

// Services
import projectNotificationsService from '@/services/projectNotifications.service';

interface Props {
  projectId: string | number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ProjectNotifications',
  props: {
    projectId: {}
  },
  setup(__props: any) {

// @ts-ignore
const props = __props;

// Composables
const { t } = useI18n();
const { notifySuccess, notifyError } = useNotifications();

// Reactive data
const loading = ref<boolean>(false);
const saving = ref<boolean>(false);
const error = ref<string | null>(null);
const hasSettings = ref<boolean>(false);
const originalData = ref<any | null>(null);

const formData = ref<{ emailNotificationsEnabled: boolean; balanceLimitNotificationsEnabled: boolean; balanceLimitThreshold: number | null }>({
  emailNotificationsEnabled: false,
  balanceLimitNotificationsEnabled: false,
  balanceLimitThreshold: null
});

const emailTypePreferences = ref<Array<{ emailType: string; enabled: boolean; lastSent: string | null }>>([]);
const validationErrors = ref<Record<string, string>>({});

// Computed properties
const currencySymbol = computed(() => {
  // You can get this from user settings or project settings
  return '$'; // Default, could be made dynamic
});

const isFormValid = computed(() => {
  return Object.keys(validationErrors.value).length === 0;
});

// Watchers
watch(
  () => formData.value,
  (newData: any) => {
    validateForm(newData);
  },
  { deep: true }
);

// Methods
const loadSettings = async () => {
  loading.value = true;
  error.value = null;
  
  try {
    const response = await projectNotificationsService.getNotificationSettings(props.projectId);
    
    if (response) {
      formData.value = {
        emailNotificationsEnabled: response.emailNotificationsEnabled,
        balanceLimitNotificationsEnabled: response.balanceLimitNotificationsEnabled,
        balanceLimitThreshold: response.balanceLimitThreshold
      };
      originalData.value = { ...formData.value };
      hasSettings.value = true;
      
      // Load email type preferences if email notifications are enabled
      if (response.emailNotificationsEnabled) {
        await loadEmailTypePreferences();
      } else {
        initializeDefaultEmailTypes();
      }
    } else {
      hasSettings.value = false;
      initializeDefaultEmailTypes();
    }
  } catch (err) {
    console.error('Error loading notification settings:', err);
    const e: any = err;
    error.value = e?.response?.data?.message || t('notifications.projectSettings.loadingError');
    hasSettings.value = false;
    initializeDefaultEmailTypes();
  } finally {
    loading.value = false;
  }
};

const validateForm = (data: any) => {
  const validation = projectNotificationsService.validateSettings(data);
  validationErrors.value = validation.errors || {};
};

const saveSettings = async () => {
  if (!isFormValid.value) return;
  
  saving.value = true;
  
  try {
    const settingsData = {
      projectId: props.projectId,
      emailNotificationsEnabled: formData.value.emailNotificationsEnabled,
      balanceLimitNotificationsEnabled: formData.value.balanceLimitNotificationsEnabled,
      balanceLimitThreshold: formData.value.balanceLimitThreshold
    };

    if (hasSettings.value) {
      await projectNotificationsService.updateNotificationSettings(props.projectId, settingsData);
    } else {
      await projectNotificationsService.createNotificationSettings(props.projectId, settingsData);
      hasSettings.value = true;
    }
    
    originalData.value = { ...formData.value };
    notifySuccess(t('notifications.projectSettings.settingsSaved'));
  } catch (err) {
    console.error('Error saving notification settings:', err);
    const e: any = err;
    notifyError(e?.response?.data?.message || t('notifications.projectSettings.settingsError'));
  } finally {
    saving.value = false;
  }
};

const resetForm = () => {
  if (originalData.value) {
    formData.value = { ...originalData.value };
  } else {
    formData.value = {
      emailNotificationsEnabled: false,
      balanceLimitNotificationsEnabled: false,
      balanceLimitThreshold: null
    };
  }
  validationErrors.value = {};
};

// Email Type Preferences Methods
const loadEmailTypePreferences = async () => {
  try {
    const preferences = await projectNotificationsService.getEmailTypePreferences(props.projectId);
    emailTypePreferences.value = preferences;
  } catch (err) {
    console.error('Error loading email type preferences:', err);
    // Initialize with default preferences if loading fails
    initializeDefaultEmailTypes();
  }
};

const initializeDefaultEmailTypes = () => {
  const defaultTypes = [
    'PROJECT_CREATION',
    'PAYMENT_CONFIRMATION', 
    'TRANSACTION_NOTIFICATION',
    'DOCUMENT_SHARING',
    'PROJECT_COLLABORATION_INVITATION',
    'PROJECT_INVITE',
    'PROJECT_INVITE_OTP',
    'TASK_NOTIFICATION',
    'BALANCE_LIMIT_NOTIFICATION'
  ];

  emailTypePreferences.value = defaultTypes.map(type => ({
    emailType: type,
    enabled: false, // All email types disabled by default - notifications should be opt-in
    lastSent: null
  }));
};

const updateEmailTypePreference = async (emailType: { emailType: string; enabled: boolean }) => {
  try {
    await projectNotificationsService.updateEmailTypePreference(
      props.projectId, 
      emailType.emailType, 
      emailType.enabled
    );
    
    notifySuccess(t('notifications.projectSettings.emailTypeUpdated'));
  } catch (err) {
    console.error('Error updating email type preference:', err);
    // Revert the change on error
    emailType.enabled = !emailType.enabled;
    const e: any = err;
    notifyError(e?.response?.data?.message || t('notifications.projectSettings.emailTypeUpdateError'));
  }
};

const getEmailTypeName = (emailType: string) => {
  const typeNames: Record<string, string> = {
    'PROJECT_CREATION': t('notifications.emailTypes.projectCreation.name'),
    'PAYMENT_CONFIRMATION': t('notifications.emailTypes.paymentConfirmation.name'),
    'TRANSACTION_NOTIFICATION': t('notifications.emailTypes.transactionNotification.name'),
    'DOCUMENT_SHARING': t('notifications.emailTypes.documentSharing.name'),
    'PROJECT_COLLABORATION_INVITATION': t('notifications.emailTypes.collaborationInvitation.name'),
    'PROJECT_INVITE': t('notifications.emailTypes.projectInvite.name'),
    'PROJECT_INVITE_OTP': t('notifications.emailTypes.projectInviteOtp.name'),
    'TASK_NOTIFICATION': t('notifications.emailTypes.taskNotification.name'),
    'BALANCE_LIMIT_NOTIFICATION': t('notifications.emailTypes.balanceLimitNotification.name')
  };
  return typeNames[emailType] ?? emailType.replace('_', ' ');
};

const getEmailTypeDescription = (emailType: string) => {
  const typeDescriptions: Record<string, string> = {
    'PROJECT_CREATION': t('notifications.emailTypes.projectCreation.description'),
    'PAYMENT_CONFIRMATION': t('notifications.emailTypes.paymentConfirmation.description'),
    'TRANSACTION_NOTIFICATION': t('notifications.emailTypes.transactionNotification.description'),
    'DOCUMENT_SHARING': t('notifications.emailTypes.documentSharing.description'),
    'PROJECT_COLLABORATION_INVITATION': t('notifications.emailTypes.collaborationInvitation.description'),
    'PROJECT_INVITE': t('notifications.emailTypes.projectInvite.description'),
    'PROJECT_INVITE_OTP': t('notifications.emailTypes.projectInviteOtp.description'),
    'TASK_NOTIFICATION': t('notifications.emailTypes.taskNotification.description'),
    'BALANCE_LIMIT_NOTIFICATION': t('notifications.emailTypes.balanceLimitNotification.description')
  };
  return typeDescriptions[emailType] ?? '';
};

const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString();
};

// Lifecycle
onMounted(() => {
  loadSettings();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(AppPageHeader, {
      subtitle: _ctx.$t('notifications.projectSettings.description')
    }, null, 8, ["subtitle"]),
    (loading.value)
      ? (_openBlock(), _createBlock(AppCard, {
          key: 0,
          class: "loading-card"
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_2, [
              _cache[3] || (_cache[3] = _createElementVNode("div", { class: "loading-spinner" }, null, -1)),
              _createElementVNode("p", _hoisted_3, _toDisplayString(_ctx.$t('common.loading')), 1)
            ])
          ]),
          _: 1
        }))
      : (error.value)
        ? (_openBlock(), _createBlock(AppCard, {
            key: 1,
            class: "error-card"
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_4, [
                _cache[4] || (_cache[4] = _createElementVNode("div", { class: "error-icon" }, [
                  _createElementVNode("i", { class: "fas fa-exclamation-triangle" })
                ], -1)),
                _createElementVNode("h3", _hoisted_5, _toDisplayString(_ctx.$t('notifications.projectSettings.loadingError')), 1),
                _createElementVNode("p", _hoisted_6, _toDisplayString(error.value), 1),
                _createVNode(AppButton, {
                  onClick: loadSettings,
                  variant: "primary",
                  size: "sm"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('common.retry')), 1)
                  ]),
                  _: 1
                })
              ])
            ]),
            _: 1
          }))
        : (_openBlock(), _createBlock(AppCard, {
            key: 2,
            class: "settings-card"
          }, {
            default: _withCtx(() => [
              _createElementVNode("form", {
                onSubmit: _withModifiers(saveSettings, ["prevent"]),
                class: "settings-form"
              }, [
                _createElementVNode("div", _hoisted_7, [
                  _createElementVNode("div", _hoisted_8, [
                    _createElementVNode("label", _hoisted_9, _toDisplayString(_ctx.$t('notifications.projectSettings.emailNotifications')), 1),
                    _createElementVNode("div", _hoisted_10, [
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((formData.value.emailNotificationsEnabled) = $event)),
                        type: "checkbox",
                        class: "toggle-input",
                        id: "email-notifications-toggle"
                      }, null, 512), [
                        [_vModelCheckbox, formData.value.emailNotificationsEnabled]
                      ]),
                      _cache[5] || (_cache[5] = _createElementVNode("label", {
                        for: "email-notifications-toggle",
                        class: "toggle-switch"
                      }, [
                        _createElementVNode("span", { class: "toggle-slider" })
                      ], -1)),
                      _createElementVNode("span", _hoisted_11, _toDisplayString(formData.value.emailNotificationsEnabled ? _ctx.$t('notifications.projectSettings.enabled') : _ctx.$t('notifications.projectSettings.disabled')), 1)
                    ])
                  ])
                ]),
                _createElementVNode("div", _hoisted_12, [
                  _createElementVNode("div", _hoisted_13, [
                    _createElementVNode("label", _hoisted_14, [
                      _createTextVNode(_toDisplayString(_ctx.$t('notifications.projectSettings.balanceLimit')) + " ", 1),
                      _createElementVNode("span", _hoisted_15, _toDisplayString(_ctx.$t('notifications.projectSettings.balanceLimitDescription')), 1)
                    ]),
                    _createElementVNode("div", _hoisted_16, [
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((formData.value.balanceLimitNotificationsEnabled) = $event)),
                        type: "checkbox",
                        class: "toggle-input",
                        id: "balance-limit-toggle",
                        disabled: !formData.value.emailNotificationsEnabled
                      }, null, 8, _hoisted_17), [
                        [_vModelCheckbox, formData.value.balanceLimitNotificationsEnabled]
                      ]),
                      _createElementVNode("label", {
                        for: "balance-limit-toggle",
                        class: _normalizeClass(["toggle-switch", { 'disabled': !formData.value.emailNotificationsEnabled }])
                      }, [...(_cache[6] || (_cache[6] = [
                        _createElementVNode("span", { class: "toggle-slider" }, null, -1)
                      ]))], 2),
                      _createElementVNode("span", _hoisted_18, _toDisplayString(formData.value.balanceLimitNotificationsEnabled ? _ctx.$t('notifications.projectSettings.enabled') : _ctx.$t('notifications.projectSettings.disabled')), 1)
                    ])
                  ]),
                  (formData.value.balanceLimitNotificationsEnabled && formData.value.emailNotificationsEnabled)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                        _createElementVNode("label", _hoisted_20, _toDisplayString(_ctx.$t('notifications.projectSettings.threshold')), 1),
                        _createElementVNode("div", _hoisted_21, [
                          _createElementVNode("span", _hoisted_22, _toDisplayString(currencySymbol.value), 1),
                          _withDirectives(_createElementVNode("input", {
                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((formData.value.balanceLimitThreshold) = $event)),
                            type: "number",
                            id: "balance-threshold",
                            class: _normalizeClass(["form-input", { 'error': validationErrors.value.balanceLimitThreshold }]),
                            placeholder: _ctx.$t('notifications.projectSettings.thresholdPlaceholder'),
                            min: "0",
                            step: "0.01"
                          }, null, 10, _hoisted_23), [
                            [
                              _vModelText,
                              formData.value.balanceLimitThreshold,
                              void 0,
                              { number: true }
                            ]
                          ])
                        ]),
                        (validationErrors.value.balanceLimitThreshold)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_24, _toDisplayString(validationErrors.value.balanceLimitThreshold), 1))
                          : _createCommentVNode("", true)
                      ]))
                    : _createCommentVNode("", true)
                ]),
                (formData.value.emailNotificationsEnabled)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                      _createElementVNode("h3", _hoisted_26, [
                        _cache[7] || (_cache[7] = _createElementVNode("i", { class: "fas fa-envelope-open-text" }, null, -1)),
                        _createTextVNode(" " + _toDisplayString(_ctx.$t('notifications.projectSettings.emailTypePreferences')), 1)
                      ]),
                      _createElementVNode("p", _hoisted_27, _toDisplayString(_ctx.$t('notifications.projectSettings.emailTypeDescription')), 1),
                      _createElementVNode("div", _hoisted_28, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(emailTypePreferences.value, (emailType) => {
                          return (_openBlock(), _createElementBlock("div", {
                            key: emailType.emailType,
                            class: "email-type-card"
                          }, [
                            _createElementVNode("div", _hoisted_29, [
                              _createElementVNode("div", _hoisted_30, [
                                _createElementVNode("h4", _hoisted_31, _toDisplayString(getEmailTypeName(emailType.emailType)), 1),
                                _createElementVNode("p", _hoisted_32, _toDisplayString(getEmailTypeDescription(emailType.emailType)), 1)
                              ]),
                              _createElementVNode("div", _hoisted_33, [
                                _createElementVNode("div", _hoisted_34, [
                                  _withDirectives(_createElementVNode("input", {
                                    "onUpdate:modelValue": ($event: any) => ((emailType.enabled) = $event),
                                    type: "checkbox",
                                    class: "toggle-input",
                                    id: `email-type-${emailType.emailType}`,
                                    onChange: ($event: any) => (updateEmailTypePreference(emailType))
                                  }, null, 40, _hoisted_35), [
                                    [_vModelCheckbox, emailType.enabled]
                                  ]),
                                  _createElementVNode("label", {
                                    for: `email-type-${emailType.emailType}`,
                                    class: "toggle-switch toggle-switch-sm"
                                  }, [...(_cache[8] || (_cache[8] = [
                                    _createElementVNode("span", { class: "toggle-slider" }, null, -1)
                                  ]))], 8, _hoisted_36),
                                  _createElementVNode("span", _hoisted_37, _toDisplayString(emailType.enabled ? _ctx.$t('notifications.projectSettings.enabled') : _ctx.$t('notifications.projectSettings.disabled')), 1)
                                ])
                              ])
                            ]),
                            (emailType.lastSent)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_38, [
                                  _createElementVNode("span", _hoisted_39, _toDisplayString(_ctx.$t('notifications.projectSettings.lastSent')) + ": " + _toDisplayString(formatDate(emailType.lastSent)), 1)
                                ]))
                              : _createCommentVNode("", true)
                          ]))
                        }), 128))
                      ])
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_40, [
                  _createVNode(AppButton, {
                    type: "submit",
                    variant: "primary",
                    loading: saving.value,
                    disabled: !isFormValid.value || saving.value
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('notifications.projectSettings.save')), 1)
                    ]),
                    _: 1
                  }, 8, ["loading", "disabled"]),
                  _createVNode(AppButton, {
                    onClick: resetForm,
                    variant: "outline",
                    disabled: saving.value
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('notifications.projectSettings.reset')), 1)
                    ]),
                    _: 1
                  }, 8, ["disabled"])
                ])
              ], 32)
            ]),
            _: 1
          }))
  ]))
}
}

})